/**
 * Mama & Moon — storefront behaviour.
 *
 * Progressive enhancement only: every feature below has a working
 * server-rendered fallback (links, regular form posts), so the storefront
 * stays usable if this file fails to load.
 *
 * The cart drawer never keeps its own copy of the cart: every change goes
 * through Shopify's Cart AJAX API and the drawer is re-rendered from the
 * `cart-drawer` section HTML that Shopify returns.
 */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const strings = theme.strings || {};
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const CART_SECTION = 'cart-drawer';

  const debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');

  const fetchSection = async (url, signal) => {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return parseHTML(await response.text());
  };

  const sectionUrl = (url, sectionId) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    return target.toString();
  };

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------------
     Toast (used for errors when there is nowhere better to show them)
  ------------------------------------------------------------------------ */
  const toast = (() => {
    const el = document.getElementById('CartToast');
    let timer;
    const hide = () => {
      if (el) el.hidden = true;
    };
    el?.querySelector('[data-toast-close]')?.addEventListener('click', hide);
    return {
      show(message) {
        if (!el) return;
        el.querySelector('[data-toast-message]').textContent = message;
        el.hidden = false;
        clearTimeout(timer);
        timer = setTimeout(hide, 6000);
      },
    };
  })();

  /* ------------------------------------------------------------------------
     Button feedback: loading spinner, then a brief "Added" state
  ------------------------------------------------------------------------ */
  const buttonState = {
    loading(button) {
      if (!button) return;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    },
    done(button, success) {
      if (!button) return;
      button.removeAttribute('aria-busy');
      if (!button.dataset.unavailable) button.disabled = false;
      if (!success) return;
      const label = button.querySelector('.button__label');
      const original = label?.textContent;
      button.dataset.state = 'success';
      if (label && strings.added) label.textContent = strings.added;
      setTimeout(() => {
        delete button.dataset.state;
        if (label && original) label.textContent = original;
      }, 1800);
    },
  };

  /* ------------------------------------------------------------------------
     Dialog helpers (focus return, backdrop click, Escape is native)
  ------------------------------------------------------------------------ */
  const openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    dialog._trigger = trigger || document.activeElement;
    dialog.showModal();
  };

  const wireDialog = (dialog) => {
    if (!dialog || dialog.dataset.wired) return;
    dialog.dataset.wired = 'true';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      const trigger = dialog._trigger;
      if (trigger && document.contains(trigger)) trigger.focus();
    });
  };

  /* ------------------------------------------------------------------------
     Cart controller
  ------------------------------------------------------------------------ */
  const cart = (() => {
    const drawer = () => document.querySelector('[data-cart-drawer]');
    const status = () => document.querySelector('[data-cart-status]');
    let busy = false;

    const announce = (message) => {
      const el = status();
      if (!el || !message) return;
      el.textContent = '';
      setTimeout(() => (el.textContent = message), 60);
    };

    const updateCounts = (count) => {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        const visible = badge.querySelector('[aria-hidden]');
        const changed = visible && visible.textContent.trim() !== String(count);
        badge.hidden = count === 0;
        if (visible) visible.textContent = count;
        const hidden = badge.querySelector('.visually-hidden');
        if (hidden) hidden.textContent = hidden.textContent.replace(/\d+/, count);
        if (changed && !prefersReducedMotion.matches) {
          badge.classList.remove('is-bumping');
          void badge.offsetWidth;
          badge.classList.add('is-bumping');
        }
      });
      document.querySelectorAll('.floating-cart').forEach((button) => {
        button.classList.toggle('floating-cart--empty', count === 0);
      });
    };

    /** Replace the drawer contents with fresh section HTML from Shopify. */
    const render = (sectionHTML) => {
      const current = drawer()?.querySelector('[data-cart-drawer-content]');
      if (!current || !sectionHTML) return;
      const fresh = parseHTML(sectionHTML).querySelector('[data-cart-drawer-content]');
      if (!fresh) return;

      // Remember focus so keyboard users don't lose their place.
      const active = document.activeElement;
      const focusLine = active?.closest?.('[data-line]')?.dataset.line;
      const focusRole = active?.matches?.('[data-cart-line-quantity]')
        ? 'qty'
        : active?.closest?.('[data-quantity-plus]')
          ? 'plus'
          : active?.closest?.('[data-quantity-minus]')
            ? 'minus'
            : null;
      const hadFocus = current.contains(active);

      current.replaceWith(fresh);
      updateCounts(Number(fresh.dataset.cartItemCount || 0));

      if (hadFocus) {
        const line = focusLine && fresh.querySelector(`[data-line="${focusLine}"]`);
        const target =
          (line &&
            (focusRole === 'qty'
              ? line.querySelector('[data-cart-line-quantity]')
              : focusRole === 'plus'
                ? line.querySelector('[data-quantity-plus]')
                : focusRole === 'minus'
                  ? line.querySelector('[data-quantity-minus]')
                  : null)) ||
          fresh.querySelector('[data-cart-close]');
        target?.focus();
      }
    };

    const showError = (message) => {
      const el = drawer()?.querySelector('[data-cart-error]');
      if (el) {
        el.textContent = message;
        el.hidden = false;
      } else {
        toast.show(message);
      }
      announce(message);
    };

    const refresh = async () => {
      try {
        const response = await fetch(sectionUrl(theme.routes.cart, CART_SECTION));
        if (response.ok) render(await response.text());
      } catch (error) {
        // Keep the current drawer; it refreshes on the next cart action.
      }
    };

    const open = (trigger) => {
      const dialog = drawer();
      if (!dialog) return false;
      openDialog(dialog, trigger);
      dialog.querySelector('[data-cart-close]')?.focus();
      return true;
    };

    const close = () => drawer()?.close();

    /** Add a product form to the cart. */
    const add = async (form) => {
      if (form.dataset.busy) return;
      const button = form.querySelector('[type="submit"][name="add"]');
      const errorEl = form.querySelector('[data-product-error]');
      form.dataset.busy = 'true';
      buttonState.loading(button);
      if (errorEl) errorEl.hidden = true;

      const body = new FormData(form);
      body.append('sections', CART_SECTION);
      body.append('sections_url', window.location.pathname);

      let success = false;
      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          const message = data.description || data.message || strings.cartError;
          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          } else {
            toast.show(message);
          }
          return;
        }
        success = true;
        if (data.sections && data.sections[CART_SECTION]) {
          render(data.sections[CART_SECTION]);
        } else {
          await refresh();
        }
        announce(`${strings.addedToCart}: ${data.product_title || data.title || ''}`);
        document.querySelector('[data-quick-view-dialog]')?.close();
        if (document.body.dataset.cartAjax === 'true') open(button);
      } catch (error) {
        // Network or parsing failure: fall back to a normal form post.
        form.removeAttribute('data-product-form');
        form.submit();
      } finally {
        delete form.dataset.busy;
        buttonState.done(button, success);
      }
    };

    /** Change a line's quantity (0 removes it). */
    const change = async (line, quantity) => {
      if (busy) return;
      busy = true;
      const content = drawer()?.querySelector('[data-cart-drawer-content]');
      content?.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity),
            sections: [CART_SECTION],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          await refresh();
          showError(data.description || data.message || strings.cartError);
          return;
        }
        render(data.sections?.[CART_SECTION]);
        announce(Number(quantity) === 0 ? strings.itemRemoved : strings.cartUpdated);
      } catch (error) {
        showError(strings.cartError);
      } finally {
        busy = false;
        drawer()?.querySelector('[data-cart-drawer-content]')?.removeAttribute('aria-busy');
      }
    };

    const init = () => {
      wireDialog(drawer());

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-open]');
        if (opener && !document.body.classList.contains('template-cart')) {
          if (open(opener)) event.preventDefault();
          return;
        }
        if (event.target.closest('[data-cart-close]')) {
          close();
          return;
        }
        const remove = event.target.closest('[data-cart-remove]');
        if (remove && drawer()?.contains(remove)) {
          event.preventDefault();
          change(remove.dataset.cartRemove, 0);
        }
      });

      const changeSoon = debounce((input) => change(input.dataset.cartLineQuantity, input.value), 450);
      document.addEventListener('change', (event) => {
        if (event.target.matches('[data-cart-line-quantity]')) changeSoon(event.target);
      });

      // Pages restored from the back/forward cache may show an old cart.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) refresh();
      });
    };

    return { init, add, open, refresh };
  })();

  /* ------------------------------------------------------------------------
     Product forms → cart controller
  ------------------------------------------------------------------------ */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    cart.add(form);
  });

  /* ------------------------------------------------------------------------
     Header: sticky shadow, dropdowns, drawer, search modal
  ------------------------------------------------------------------------ */
  const initHeader = (root = document) => {
    const header = root.querySelector('[data-site-header]');
    if (header && !header.dataset.ready) {
      header.dataset.ready = 'true';
      const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });

      const dropdowns = header.querySelectorAll('[data-dropdown]');
      dropdowns.forEach((dropdown) => {
        dropdown.addEventListener('toggle', () => {
          if (!dropdown.open) return;
          dropdowns.forEach((other) => {
            if (other !== dropdown) other.open = false;
          });
        });
        dropdown.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && dropdown.open) {
            dropdown.open = false;
            dropdown.querySelector('summary')?.focus();
          }
        });
        dropdown.addEventListener('focusout', (event) => {
          if (dropdown.open && !dropdown.contains(event.relatedTarget)) dropdown.open = false;
        });

        // Open on hover for mouse users (click and keyboard still work).
        let hoverTimer;
        dropdown.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !desktop.matches) return;
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(() => (dropdown.open = true), 120);
        });
        dropdown.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(() => (dropdown.open = false), 220);
        });
      });
      document.addEventListener('click', (event) => {
        dropdowns.forEach((dropdown) => {
          if (dropdown.open && !dropdown.contains(event.target)) dropdown.open = false;
        });
      });
    }

    // Drawers (mobile menu)
    root.querySelectorAll('[data-drawer-open]').forEach((button) => {
      const dialog = document.getElementById(button.dataset.drawerOpen);
      if (!dialog || button.dataset.ready) return;
      button.dataset.ready = 'true';
      wireDialog(dialog);
      button.addEventListener('click', () => {
        openDialog(dialog, button);
        button.setAttribute('aria-expanded', 'true');
      });
      dialog.addEventListener('close', () => button.setAttribute('aria-expanded', 'false'));
      dialog.querySelectorAll('[data-drawer-close]').forEach((close) => {
        close.addEventListener('click', () => dialog.close());
      });
    });

    // Search modal
    const searchModal = document.getElementById('SearchModal');
    if (searchModal && !searchModal.dataset.ready) {
      searchModal.dataset.ready = 'true';
      wireDialog(searchModal);
      initPredictiveSearch(searchModal);
      searchModal.querySelector('[data-search-close]')?.addEventListener('click', () => searchModal.close());
    }
    root.querySelectorAll('[data-search-open]').forEach((trigger) => {
      if (trigger.dataset.ready || !searchModal) return;
      trigger.dataset.ready = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        document.getElementById('MenuDrawer')?.close();
        openDialog(searchModal, trigger);
        searchModal.querySelector('[data-predictive-input]')?.focus();
      });
    });
  };

  /* ------------------------------------------------------------------------
     Predictive search
  ------------------------------------------------------------------------ */
  const initPredictiveSearch = (container) => {
    const input = container.querySelector('[data-predictive-input]');
    const results = container.querySelector('[data-predictive-results]');
    if (!input || !results || !theme.routes.predictiveSearch) return;
    let controller;

    const render = debounce(async () => {
      const terms = input.value.trim();
      controller?.abort();
      if (!terms) {
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      controller = new AbortController();
      results.setAttribute('aria-busy', 'true');
      const params = new URLSearchParams({
        q: terms,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,article,query',
        'resources[limit]': '6',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      });
      try {
        const html = await fetchSection(`${theme.routes.predictiveSearch}?${params}`, controller.signal);
        const content = html.querySelector('.predictive-search__results');
        results.innerHTML = content ? content.outerHTML : '';
        input.setAttribute('aria-expanded', content ? 'true' : 'false');
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '';
      } finally {
        results.removeAttribute('aria-busy');
      }
    }, 250);

    input.addEventListener('input', render);
  };

  /* ------------------------------------------------------------------------
     Announcement bar rotation
  ------------------------------------------------------------------------ */
  const initAnnouncements = (root = document) => {
    root.querySelectorAll('[data-announcements]').forEach((bar) => {
      const items = [...bar.querySelectorAll('.announcement-bar__item')];
      if (items.length < 2 || bar.dataset.ready || prefersReducedMotion.matches) return;
      bar.dataset.ready = 'true';
      let index = 0;
      let paused = false;
      bar.addEventListener('mouseenter', () => (paused = true));
      bar.addEventListener('mouseleave', () => (paused = false));
      bar.addEventListener('focusin', () => (paused = true));
      bar.addEventListener('focusout', () => (paused = false));
      setInterval(() => {
        if (paused) return;
        items[index].hidden = true;
        index = (index + 1) % items.length;
        items[index].hidden = false;
      }, Number(bar.dataset.interval) || 5000);
    });
  };

  /* ------------------------------------------------------------------------
     Quantity steppers (product page, cart page, cart drawer)
  ------------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (!button) return;
    const input = button.closest('[data-quantity]')?.querySelector('input');
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = input.min === '' ? 0 : Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    const current = Number(input.value) || 0;
    const next = button.hasAttribute('data-quantity-plus') ? current + step : current - step;
    const clamped = Math.min(max, Math.max(min, next));
    if (clamped === current) return;
    input.value = clamped;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------------
     Cart page: update automatically after quantity changes
  ------------------------------------------------------------------------ */
  const initCartPage = (root = document) => {
    const form = root.querySelector('[data-cart-form]');
    if (!form || form.dataset.ready) return;
    form.dataset.ready = 'true';
    const updateButton = form.querySelector('[data-cart-update]');
    const submit = debounce(() => {
      form.setAttribute('aria-busy', 'true');
      if (form.requestSubmit && updateButton) {
        form.requestSubmit(updateButton);
      } else {
        updateButton?.click();
      }
    }, 700);
    form.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-quantity]')) submit();
    });
  };

  /* ------------------------------------------------------------------------
     Product gallery
  ------------------------------------------------------------------------ */
  const initGallery = (section) => {
    const gallery = section.querySelector('[data-gallery]');
    const track = gallery?.querySelector('[data-gallery-track]');
    if (!track || gallery.dataset.ready) return null;
    gallery.dataset.ready = 'true';

    const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
    const counter = gallery.querySelector('[data-gallery-current]');
    const isGrid = () => desktop.matches && !section.classList.contains('quick-view');

    const setActive = (mediaId) => {
      thumbs.forEach((thumb) => {
        const active = thumb.dataset.thumb === String(mediaId);
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const slides = [...track.children];
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (counter && index > -1) counter.textContent = index + 1;
    };

    const goTo = (mediaId) => {
      const slide = track.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      if (isGrid()) {
        // On desktop all images are visible: bring the selected one to the top.
        if (track.firstElementChild !== slide) {
          track.prepend(slide);
          slide.classList.remove('is-entering');
          void slide.offsetWidth;
          slide.classList.add('is-entering');
        }
      } else {
        track.scrollTo({
          left: slide.offsetLeft - track.offsetLeft,
          behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
        });
      }
      setActive(mediaId);
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(thumb.dataset.thumb)));

    track.addEventListener(
      'scroll',
      debounce(() => {
        if (isGrid()) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        const slide = track.children[index];
        if (slide) setActive(slide.dataset.mediaId);
      }, 80),
      { passive: true }
    );

    return { goTo };
  };

  /* ------------------------------------------------------------------------
     Product section: variant picker, sticky bar (also used by quick view)
  ------------------------------------------------------------------------ */
  const initProductSection = (section) => {
    if (section.dataset.ready) return;
    section.dataset.ready = 'true';

    const gallery = initGallery(section);
    initStickyBuy(section);
    const picker = section.querySelector('[data-variant-picker]');
    if (!picker) return;

    let variants = [];
    try {
      variants = JSON.parse(picker.querySelector('[data-variants-json]').textContent);
    } catch (error) {
      return;
    }

    const fieldsets = [...picker.querySelectorAll('[data-option-index]')];
    const addButton = section.querySelector('[data-add-button]');
    const addLabel = section.querySelector('[data-add-label]');
    const stickyButton = section.querySelector('[data-sticky-add]');
    let controller;

    const selectedOptions = () =>
      fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value ?? null);

    const updateAvailability = (selected) => {
      fieldsets.forEach((fieldset, optionIndex) => {
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = variants.some(
            (variant) =>
              variant.available &&
              variant.options[optionIndex] === input.value &&
              selected.every((value, index) => index === optionIndex || variant.options[index] === value)
          );
          input.dataset.unavailable = available ? 'false' : 'true';
          const label = fieldset.querySelector(`label[for="${input.id}"] [data-unavailable-label]`);
          if (label) label.hidden = available;
        });
        const legendValue = fieldset.querySelector('[data-selected-value]');
        if (legendValue) legendValue.textContent = selected[optionIndex] ?? '';
      });
    };

    /* Update the stock indicator straight away from the variant data; the
       server render that follows refines it (e.g. "available to order"). */
    const setStock = (variant) => {
      const status = section.querySelector('[data-inventory]');
      if (!status) return;
      const state = !variant ? 'unavailable' : variant.available ? 'in' : 'out';
      const labels = { in: strings.stockIn, out: strings.stockOut, unavailable: strings.unavailable };
      status.className = status.className.replace(/stock-status--[\w-]+/, `stock-status--${state === 'unavailable' ? 'out' : state}`);
      status.dataset.state = state;
      const label = status.querySelector('[data-inventory-label]');
      if (label && labels[state]) label.textContent = labels[state];
    };

    const setButtons = (variant) => {
      const available = Boolean(variant && variant.available);
      const text = !variant ? strings.unavailable : available ? strings.addToCart : strings.soldOut;
      [addButton, stickyButton].forEach((button) => {
        if (!button) return;
        button.disabled = !available;
        if (available) delete button.dataset.unavailable;
        else button.dataset.unavailable = 'true';
      });
      if (addLabel) addLabel.textContent = text;
      if (stickyButton) stickyButton.textContent = text;
    };

    const refreshFromServer = async (variant) => {
      controller?.abort();
      controller = new AbortController();
      const url = new URL(section.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', section.dataset.sectionId);
      try {
        const html = await fetchSection(url.toString(), controller.signal);
        ['[data-price-container]', '.product__installments', '[data-sticky-price]'].forEach((selector) => {
          const current = section.querySelector(selector);
          const fresh = html.querySelector(selector);
          if (current && fresh) current.innerHTML = fresh.innerHTML;
        });
        const status = section.querySelector('[data-inventory]');
        const freshStatus = html.querySelector('[data-inventory]');
        if (status && freshStatus) {
          status.className = freshStatus.className;
          status.dataset.state = freshStatus.dataset.state;
          status.innerHTML = freshStatus.innerHTML;
        }
        const quantity = section.querySelector('.quantity__input');
        const freshQuantity = html.querySelector('.quantity__input');
        if (quantity && freshQuantity) {
          ['min', 'max', 'step'].forEach((attr) => {
            if (freshQuantity.hasAttribute(attr)) quantity.setAttribute(attr, freshQuantity.getAttribute(attr));
            else quantity.removeAttribute(attr);
          });
        }
      } catch (error) {
        // Price stays as rendered; the variant ID is already correct for checkout.
      }
    };

    picker.addEventListener('change', () => {
      const selected = selectedOptions();
      const variant = variants.find((candidate) =>
        candidate.options.every((value, index) => value === selected[index])
      );

      updateAvailability(selected);
      setButtons(variant);
      setStock(variant);
      if (!variant) return;

      section.querySelectorAll('form input[name="id"]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if (section.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (variant.featured_media && gallery) gallery.goTo(variant.featured_media.id);
      refreshFromServer(variant);
    });

    updateAvailability(selectedOptions());
  };

  /* Mobile add-to-cart bar: appears once the main button scrolls away. */
  const initStickyBuy = (section) => {
    const bar = section.querySelector('[data-sticky-buy]');
    const mainButton = section.querySelector('[data-add-button]');
    if (!bar || !mainButton) return;
    const form = mainButton.form;
    let shown = false;

    const update = () => {
      const show = !desktop.matches && mainButton.getBoundingClientRect().bottom < 0;
      if (show === shown) return;
      shown = show;
      bar.hidden = !show;
      document.body.classList.toggle('has-sticky-buy', show);
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', debounce(update, 150));
    update();

    bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      if (!form || mainButton.disabled) return;
      if (form.requestSubmit) form.requestSubmit(mainButton);
      else mainButton.click();
    });
  };

  /* ------------------------------------------------------------------------
     Quick view
  ------------------------------------------------------------------------ */
  const quickView = (() => {
    const dialog = () => document.querySelector('[data-quick-view-dialog]');
    let controller;

    const open = async (url, trigger) => {
      const el = dialog();
      if (!el) return false;
      const content = el.querySelector('[data-quick-view-content]');
      wireDialog(el);
      controller?.abort();
      controller = new AbortController();
      content.innerHTML = `<div class="quick-view__loading"><span class="spinner" aria-hidden="true"></span><span class="visually-hidden">${strings.loading || ''}</span></div>`;
      openDialog(el, trigger);
      el.querySelector('[data-quick-view-close]')?.focus();

      try {
        const html = await fetchSection(sectionUrl(url, 'quick-view'), controller.signal);
        const fresh = html.querySelector('.quick-view');
        if (!fresh) throw new Error('No quick view content');
        content.innerHTML = '';
        content.append(fresh);
        initProductSection(fresh);
        fresh.querySelector('h2')?.setAttribute('tabindex', '-1');
        fresh.querySelector('h2')?.focus();
      } catch (error) {
        if (error.name === 'AbortError') return true;
        const message = document.createElement('p');
        const link = document.createElement('a');
        message.className = 'quick-view__error';
        link.href = url;
        link.textContent = strings.quickViewError || url;
        message.append(link);
        content.replaceChildren(message);
      }
      return true;
    };

    const init = () => {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-quick-view]');
        if (trigger) {
          event.preventDefault();
          open(trigger.dataset.quickView, trigger);
          return;
        }
        if (event.target.closest('[data-quick-view-close]')) dialog()?.close();
      });
      dialog()?.addEventListener('close', () => {
        controller?.abort();
        const content = dialog().querySelector('[data-quick-view-content]');
        setTimeout(() => (content.innerHTML = ''), 200);
      });
    };

    return { init };
  })();

  /* ------------------------------------------------------------------------
     Product details tabs: accordions on mobile, tabs on desktop
  ------------------------------------------------------------------------ */
  const initDetailsTabs = (root = document) => {
    root.querySelectorAll('[data-tabs]').forEach((container) => {
      if (container.dataset.ready) return;
      const items = [...container.querySelectorAll(':scope > details')];
      if (items.length < 2) return;
      container.dataset.ready = 'true';
      const uid = Math.random().toString(36).slice(2, 8);
      let tablist;

      const select = (index, focus = false) => {
        tablist.querySelectorAll('[role="tab"]').forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          if (selected && focus) tab.focus();
        });
        items.forEach((item, i) => {
          item.open = true;
          item.classList.toggle('is-active-tab', i === index);
        });
      };

      const enable = () => {
        if (tablist) return;
        tablist = document.createElement('div');
        tablist.className = 'product-tabs__list';
        tablist.setAttribute('role', 'tablist');
        items.forEach((item, index) => {
          const panel = item.querySelector('.accordion__content');
          panel.id = panel.id || `tabpanel-${uid}-${index}`;
          panel.setAttribute('role', 'tabpanel');
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'product-tabs__tab';
          tab.id = `tab-${uid}-${index}`;
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', panel.id);
          tab.textContent = item.querySelector('summary span')?.textContent.trim() || '';
          panel.setAttribute('aria-labelledby', tab.id);
          tab.addEventListener('click', () => select(index));
          tablist.append(tab);
        });
        tablist.addEventListener('keydown', (event) => {
          const tabs = [...tablist.children];
          const current = tabs.indexOf(document.activeElement);
          if (current < 0) return;
          let next = null;
          if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== null) {
            event.preventDefault();
            select(next, true);
          }
        });
        container.prepend(tablist);
        container.classList.add('is-tabbed');
        const openIndex = Math.max(0, items.findIndex((item) => item.open));
        select(openIndex);
      };

      const disable = () => {
        if (!tablist) return;
        tablist.remove();
        tablist = null;
        container.classList.remove('is-tabbed');
        items.forEach((item, i) => {
          item.classList.remove('is-active-tab');
          item.open = i === 0;
          const panel = item.querySelector('.accordion__content');
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
        });
      };

      const sync = () => (desktop.matches ? enable() : disable());
      sync();
      desktop.addEventListener('change', sync);
    });
  };

  /* ------------------------------------------------------------------------
     Simple tablists (gift finder)
  ------------------------------------------------------------------------ */
  const initTablists = (root = document) => {
    root.querySelectorAll('[data-tablist]').forEach((tablist) => {
      if (tablist.dataset.ready) return;
      tablist.dataset.ready = 'true';
      const tabs = [...tablist.querySelectorAll('[role="tab"]')];
      const select = (index, focus) => {
        tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
          if (selected && focus) tab.focus();
        });
      };
      tabs.forEach((tab, index) => tab.addEventListener('click', () => select(index, false)));
      tablist.addEventListener('keydown', (event) => {
        const current = tabs.indexOf(document.activeElement);
        if (current < 0) return;
        let next = null;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next !== null) {
          event.preventDefault();
          select(next, true);
        }
      });
    });
  };

  /* ------------------------------------------------------------------------
     Carousels (featured products)
  ------------------------------------------------------------------------ */
  const initCarousels = (root = document) => {
    root.querySelectorAll('[data-carousel-controls]').forEach((controls) => {
      if (controls.dataset.ready) return;
      const track = document.getElementById(controls.dataset.carouselControls);
      if (!track) return;
      controls.dataset.ready = 'true';
      const prev = controls.querySelector('[data-carousel-prev]');
      const next = controls.querySelector('[data-carousel-next]');
      const step = () => (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth) + 16;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
        controls.hidden = track.scrollWidth <= track.clientWidth + 2;
      };
      const scroll = (direction) =>
        track.scrollBy({ left: direction * step(), behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
      prev.addEventListener('click', () => scroll(-1));
      next.addEventListener('click', () => scroll(1));
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  };

  /* ------------------------------------------------------------------------
     Product recommendations
  ------------------------------------------------------------------------ */
  const initRecommendations = (root = document) => {
    root.querySelectorAll('[data-recommendations]').forEach(async (container) => {
      if (container.dataset.ready || !container.dataset.url) return;
      container.dataset.ready = 'true';
      try {
        const html = await fetchSection(container.dataset.url);
        const fresh = html.querySelector('[data-recommendations]');
        if (fresh && fresh.querySelector('.product-grid')) {
          container.innerHTML = fresh.innerHTML;
          container.removeAttribute('data-empty');
          initReveal(container);
        }
      } catch (error) {
        // Leave the section empty.
      }
    });
  };

  /* ------------------------------------------------------------------------
     Recently viewed (stored only in this browser)
  ------------------------------------------------------------------------ */
  const RECENT_KEY = 'mama-moon:recently-viewed';
  const readRecent = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
    } catch (error) {
      return [];
    }
  };

  const recordRecent = () => {
    const section = document.querySelector('.product-section[data-product-url]');
    if (!section) return;
    const url = section.dataset.productUrl.split('?')[0];
    const list = [url, ...readRecent().filter((item) => item !== url)].slice(0, 12);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (error) {
      // Storage unavailable (private mode): the section simply stays hidden.
    }
  };

  const initRecentlyViewed = async (root = document) => {
    const section = root.querySelector('[data-recently-viewed-section]');
    if (!section || section.dataset.ready) return;
    section.dataset.ready = 'true';
    const list = section.querySelector('[data-recently-viewed-list]');
    const limit = Number(section.dataset.limit) || 4;
    const urls = readRecent()
      .filter((url) => url !== section.dataset.current)
      .slice(0, limit);
    if (!urls.length) return;

    const cards = await Promise.all(
      urls.map(async (url) => {
        try {
          const html = await fetchSection(sectionUrl(url, 'card-product'));
          return html.querySelector('.product-card');
        } catch (error) {
          return null;
        }
      })
    );
    cards.filter(Boolean).forEach((card) => {
      const item = document.createElement('li');
      item.className = 'product-grid__item';
      item.append(card);
      list.append(item);
    });
    if (list.children.length) section.hidden = false;
  };

  /* ------------------------------------------------------------------------
     Filters & sorting
  ------------------------------------------------------------------------ */
  const initFacets = (root = document) => {
    const form = root.querySelector('[data-facets-form]');
    if (!form || form.dataset.ready) return;
    form.dataset.ready = 'true';

    const panel = form.querySelector('[data-facets-panel]');
    const toggle = form.querySelector('[data-facets-toggle]');

    const open = () => {
      panel.classList.add('is-open');
      toggle?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('scroll-locked');
      panel.querySelector('button, a, input, summary')?.focus();
    };
    const close = () => {
      panel.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('scroll-locked');
    };

    if (panel) {
      toggle?.addEventListener('click', open);
      panel.querySelector('[data-facets-close]')?.addEventListener('click', () => {
        close();
        toggle?.focus();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && panel.classList.contains('is-open')) {
          close();
          toggle?.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (panel.classList.contains('is-open') && !panel.contains(event.target) && !toggle?.contains(event.target)) {
          close();
        }
      });
    }

    const navigate = () => {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        if (value !== '') params.append(key, value);
      }
      form.setAttribute('aria-busy', 'true');
      window.location.assign(`${form.getAttribute('action')}?${params.toString()}`);
    };
    const navigateSoon = debounce(navigate, 900);

    form.addEventListener('change', (event) => {
      // On mobile, filters apply with the drawer's "Apply" button; sorting applies straight away.
      if (panel?.classList.contains('is-open') && !desktop.matches && !event.target.matches('[data-facets-sort]')) return;
      if (event.target.type === 'number') navigateSoon();
      else navigate();
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      navigate();
    });
  };

  /* ------------------------------------------------------------------------
     Auto-submitting selects (country / language)
  ------------------------------------------------------------------------ */
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-autosubmit]')) event.target.form?.submit();
  });

  /* ------------------------------------------------------------------------
     Reveal on scroll
  ------------------------------------------------------------------------ */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;

  function initReveal(root = document) {
    root.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
      [...group.children].forEach((child, index) => child.style.setProperty('--i', Math.min(index, 8)));
    });
    const items = root.querySelectorAll('[data-reveal]:not(.is-revealed), [data-reveal-stagger]:not(.is-revealed)');
    const designMode = window.Shopify && window.Shopify.designMode;
    items.forEach((item) => {
      if (!revealObserver || designMode || prefersReducedMotion.matches) {
        item.classList.add('is-revealed');
      } else {
        revealObserver.observe(item);
      }
    });
  }

  /* ------------------------------------------------------------------------
     Init (and re-init inside the theme editor)
  ------------------------------------------------------------------------ */
  const init = (root = document) => {
    initHeader(root);
    initAnnouncements(root);
    initCartPage(root);
    initFacets(root);
    initRecommendations(root);
    initRecentlyViewed(root);
    initDetailsTabs(root);
    initTablists(root);
    initCarousels(root);
    root.querySelectorAll('[data-product-section]').forEach(initProductSection);
    if (root.matches?.('[data-product-section]')) initProductSection(root);
    initReveal(root);
  };

  const start = () => {
    cart.init();
    quickView.init();
    recordRecent();
    init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-cart-drawer]')) wireDialog(event.target.querySelector('[data-cart-drawer]'));
    init(event.target);
  });
})();
